import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientsAPI, vehiclesAPI, sitesAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateBritish } from '../utils/dateUtils';
import './ClientDetails.css';
import notesIcon from '../assets/icons/sticky-notes.png';
import NamedDriverPolicyBanner from '../components/NamedDriverPolicyBanner';
import vehicleIcon from '../assets/icons/vehicle.png';
import sitesIcon from '../assets/icons/sites.png';
import usersIcon from '../assets/icons/group.png';
import insuranceIcon from '../assets/icons/insurance.png';

type Tab = 'vehicles' | 'sites' | 'users' | 'cover-settings' | 'notes';

interface ClientVehicle {
  id: number;
  registration: string;
  make: string;
  model: string;
  year: string;
  coverStatus: string;
  vehicleValue?: number;
  inceptionDate?: string;
}
interface RenewalVehicle extends ClientVehicle {
  vehicleType?: string;
  grossVehicleWeightKg?: number | null;
  siteName: string;
  isTemporaryCover: boolean;
  isEligible: boolean;
}
interface PolicyPeriod {
  id: number;
  periodNumber: number;
  policyStartDate: string;
  policyEndDate: string;
  status: 'Current' | 'Archived' | 'Scheduled';
  archivedAt?: string | null;
}

interface ClientSite {
  id: number;
  name: string;
  addressLine1: string;
  city: string;
  postcode: string;
  isActive?: boolean;
}

interface ClientUser {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
}

const ClientDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const clientId = Number(id);
  const { isMTA, user } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin';
  const isOnboarder = user?.role === 'Onboarder';
  const canEditOnboarding = !isMTA || isOnboarder;
  const canViewPolicyArchives = ['Admin', 'SuperUser', 'SuperAdmin', 'MTA', 'Onboarder'].includes(user?.role ?? '');

  // Page-level
  const [pageLoading, setPageLoading] = useState(true);
  const [clientData, setClientData] = useState<any>(null);

  // Policy and portal-access schedule
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    policyStartDate: '',
    policyEndDate: '',
    myshieldAccessStartDate: '',
    myshieldAccessEndDate: '',
  });
  const [policyPeriods, setPolicyPeriods] = useState<PolicyPeriod[]>([]);
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [archiveSnapshot, setArchiveSnapshot] = useState<any>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [showRenewal, setShowRenewal] = useState(false);
  const [renewalReview, setRenewalReview] = useState(false);
  const [renewingPolicy, setRenewingPolicy] = useState(false);
  const [renewalError, setRenewalError] = useState<string | null>(null);
  const [renewalForm, setRenewalForm] = useState({
    policyStartDate: '', policyEndDate: '', policyReference: '', insurerName: '', coverType: '', carryForwardActiveVehicles: true,
  });
  const [renewalVehicles, setRenewalVehicles] = useState<RenewalVehicle[]>([]);
  const [selectedRenewalVehicleIds, setSelectedRenewalVehicleIds] = useState<number[]>([]);
  const [renewalVehiclesLoading, setRenewalVehiclesLoading] = useState(false);

  // Logo
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoSuccess, setLogoSuccess] = useState(false);

  // Tabs
  const [activeTab, setActiveTab] = useState<Tab>('vehicles');

  // Vehicles
  const [vehicles, setVehicles] = useState<ClientVehicle[]>([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [vehiclesFetched, setVehiclesFetched] = useState(false);

  // Sites
  const [sites, setSites] = useState<ClientSite[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesFetched, setSitesFetched] = useState(false);
  const [showAddSite, setShowAddSite] = useState(false);
  const [siteForm, setSiteForm] = useState({ name: '', addressLine1: '', city: '', postcode: '' });

  // Users
  const [users, setUsers] = useState<ClientUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersFetched, setUsersFetched] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [userForm, setUserForm] = useState({ username: '', email: '', password: '', firstName: '', lastName: '' });

  // Insurer edit
  const [editingInsurer, setEditingInsurer] = useState(false);
  const [insurerForm, setInsurerForm] = useState({ insurerName: '', insurerMidtermEmail: '', fleetAtlasContactEmail: '' });
  const [insurerSaving, setInsurerSaving] = useState(false);
  const [insurerError, setInsurerError] = useState<string | null>(null);

  // Cover settings
  const [instantCoverEnabled, setInstantCoverEnabled] = useState(false);
  const [requiresVehicleCategory, setRequiresVehicleCategory] = useState(false);
  const [showDriverPolicyBanner, setShowDriverPolicyBanner] = useState(true);
  const [trailersEnabled, setTrailersEnabled] = useState(false);
  const [claimsEnabled, setClaimsEnabled] = useState(false);
  const [claimsInboxEmail, setClaimsInboxEmail] = useState('');
  const [claimsNotificationRecipientUserIds, setClaimsNotificationRecipientUserIds] = useState<string[]>([]);
  const [mtaValueThreshold, setMtaValueThreshold] = useState<number | null>(null);
  const [coverSaving, setCoverSaving] = useState(false);
  const [coverSaved, setCoverSaved] = useState(false);
  const [coverError, setCoverError] = useState<string | null>(null);
  const [coverFetched, setCoverFetched] = useState(false);

  // Cover letter details (PDF fields)
  const [editingCoverLetter, setEditingCoverLetter] = useState(false);
  const [coverLetterForm, setCoverLetterForm] = useState({ policyReference: '', businessDescription: '', coverType: '' });
  const [coverLetterSaving, setCoverLetterSaving] = useState(false);
  const [coverLetterError, setCoverLetterError] = useState<string | null>(null);

  // Named driver policy
  const [editingPolicy, setEditingPolicy] = useState(false);
  const [policyForm, setPolicyForm] = useState('');
  const [policySaving, setPolicySaving] = useState(false);
  const [policyError, setPolicyError] = useState<string | null>(null);

  // Login background video
  const [loginVideoFileName, setLoginVideoFileName] = useState<string | null>(null);
  const [loginVideoUploading, setLoginVideoUploading] = useState(false);
  const [loginVideoError, setLoginVideoError] = useState<string | null>(null);
  const [loginVideoSuccess, setLoginVideoSuccess] = useState(false);
  const loginVideoInputRef = useRef<HTMLInputElement>(null);

  // Fleet vehicle / throbber image
  const [vehicleImageBase64, setVehicleImageBase64] = useState<string | null>(null);
  const [vehicleImageUploading, setVehicleImageUploading] = useState(false);
  const [vehicleImageError, setVehicleImageError] = useState<string | null>(null);
  const [vehicleImageSuccess, setVehicleImageSuccess] = useState(false);
  const vehicleImageInputRef = useRef<HTMLInputElement>(null);

  // Notes
  const [notes, setNotes] = useState('');
  const [newNote, setNewNote] = useState('');

  // ─── On mount ─────────────────────────────────────────────────────────────────
  // Strategy: render the page as soon as getById completes (fast JSON call).
  // Logo and vehicles are fired concurrently but do NOT block the page render —
  // they update their own state slices when they arrive.
  useEffect(() => {
    // 1. Critical path — renders the page
    const loadClientData = async () => {
      setPageLoading(true);
      try {
        const res = await clientsAPI.getById(clientId);
        const d = res.data;
        setClientData(d);
        setNotes(d.notes || '');
        setInstantCoverEnabled(d.instantCoverEnabled || false);
        setRequiresVehicleCategory(d.requiresVehicleCategory || false);
        setShowDriverPolicyBanner(d.showDriverPolicyBanner !== false);
        setTrailersEnabled(d.trailersEnabled === true);
        setClaimsEnabled(d.claimsEnabled === true);
        setClaimsInboxEmail(d.claimsInboxEmail || '');
        setClaimsNotificationRecipientUserIds((d.claimsNotificationRecipientUserIds || '').split(',').filter(Boolean));
        setMtaValueThreshold(d.mtaValueThreshold ?? null);
        setInsurerForm({
          insurerName: d.insurerName || '',
          insurerMidtermEmail: d.insurerMidtermEmail || '',
          fleetAtlasContactEmail: d.fleetAtlasContactEmail || '',
        });
        setCoverLetterForm({
          policyReference: d.policyReference || '',
          businessDescription: d.businessDescription || '',
          coverType: d.coverType || '',
        });
        setPolicyForm(d.namedDriverPolicy || '');
      } catch (err) {
        console.error('Failed to load client', err);
      } finally {
        setPageLoading(false); // render immediately — don't wait for logo/vehicles
      }
    };

    // 2. Background — logo (may be slow if blob storage is cold)
    const loadLogo = async () => {
      try {
        const res = await clientsAPI.getLogo(clientId);
        setLogoBase64(res.data.logoBase64 || null);
      } catch {
        // no logo is fine
      }
    };

    // 3. Background — vehicles for default tab
    const loadVehicles = async () => {
      setVehiclesLoading(true);
      try {
        const res = await vehiclesAPI.getByClient(clientId);
        setVehicles(res.data.filter((v: any) => v.isActive !== false));
        setVehiclesFetched(true);
      } catch (err) {
        console.error('Error loading vehicles', err);
      } finally {
        setVehiclesLoading(false);
      }
    };

    loadClientData();
    loadLogo();     // fire-and-forget
    loadVehicles(); // fire-and-forget
    if (canViewPolicyArchives) {
      setPeriodsLoading(true);
      clientsAPI.getPolicyPeriods(clientId)
        .then(res => setPolicyPeriods(Array.isArray(res.data) ? res.data : []))
        .catch(() => setArchiveError('Policy period history is unavailable.'))
        .finally(() => setPeriodsLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // ─── Save helper — uses cached clientData, no extra getById call ─────────────
  const saveClientPatch = async (patch: any) => {
    const updated = { ...clientData, ...patch };
    await clientsAPI.update(clientId, updated);
    setClientData(updated);
    return updated;
  };

  const isAwaitingInception = (vehicle: ClientVehicle) =>
    vehicle.coverStatus === 'Active' &&
    !!vehicle.inceptionDate &&
    new Date(vehicle.inceptionDate).getTime() > Date.now();

  const parseApiTimestamp = (value: string) => {
    const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(value);
    return new Date(hasTimezone ? value : `${value}Z`);
  };

  const getLondonParts = (date: Date) => Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(date)
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, part.value])
  ) as Record<string, string>;

  const toLondonInputValue = (value: string | null | undefined) => {
    if (!value) return '';
    const date = parseApiTimestamp(value);
    if (Number.isNaN(date.getTime())) return '';
    const parts = getLondonParts(date);
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  };

  const formatLondonDateTime = (value: string | null | undefined) => {
    if (!value) return null;
    const date = parseApiTimestamp(value);
    if (Number.isNaN(date.getTime())) return null;
    const parts = getLondonParts(date);
    return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute} UK time (GMT/BST)`;
  };

  const londonInputToIso = (value: string) => {
    if (!value) return null;
    const [datePart, timePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    const utcGuess = Date.UTC(year, month - 1, day, hour, minute);
    const observedLondon = getLondonParts(new Date(utcGuess));
    const observedAsUtc = Date.UTC(
      Number(observedLondon.year), Number(observedLondon.month) - 1,
      Number(observedLondon.day), Number(observedLondon.hour), Number(observedLondon.minute)
    );
    return new Date(utcGuess - (observedAsUtc - utcGuess)).toISOString();
  };

  const startScheduleEdit = () => {
    setScheduleForm({
      policyStartDate: toLondonInputValue(clientData.policyStartDate),
      policyEndDate: toLondonInputValue(clientData.policyEndDate),
      myshieldAccessStartDate: toLondonInputValue(clientData.myshieldAccessStartDate),
      myshieldAccessEndDate: toLondonInputValue(clientData.myshieldAccessEndDate),
    });
    setScheduleError(null);
    setEditingSchedule(true);
  };

  const handleSaveSchedule = async () => {
    const policyStart = londonInputToIso(scheduleForm.policyStartDate);
    const policyEnd = londonInputToIso(scheduleForm.policyEndDate);
    const accessStart = londonInputToIso(scheduleForm.myshieldAccessStartDate);
    const accessEnd = londonInputToIso(scheduleForm.myshieldAccessEndDate);

    if (policyStart && policyEnd && new Date(policyEnd) <= new Date(policyStart)) {
      setScheduleError('Policy end must be after the policy start.');
      return;
    }
    if (accessStart && accessEnd && new Date(accessEnd) <= new Date(accessStart)) {
      setScheduleError('Myshield access end must be after the access start.');
      return;
    }

    setScheduleSaving(true);
    setScheduleError(null);
    try {
      await saveClientPatch({
        policyStartDate: policyStart,
        policyEndDate: policyEnd,
        myshieldAccessStartDate: accessStart,
        myshieldAccessEndDate: accessEnd,
      });
      setEditingSchedule(false);
    } catch {
      setScheduleError('Failed to save the policy and Myshield access schedule.');
    } finally {
      setScheduleSaving(false);
    }
  };

  const viewArchiveSnapshot = async (period: PolicyPeriod) => {
    setArchiveError(null);
    try {
      const response = await clientsAPI.getPolicyPeriodSnapshot(clientId, period.id);
      setArchiveSnapshot(response.data);
    } catch (err: any) {
      setArchiveError(err.response?.data?.message || 'Unable to load the archived policy snapshot.');
    }
  };

  const startRenewal = () => {
    setRenewalForm({
      policyStartDate: '',
      policyEndDate: '',
      policyReference: clientData.policyReference || '',
      insurerName: clientData.insurerName || '',
      coverType: clientData.coverType || '',
      carryForwardActiveVehicles: true,
    });
    setRenewalError(null);
    setRenewalReview(false);
    setShowRenewal(true);
    setRenewalVehiclesLoading(true);
    clientsAPI.getRenewalPreview(clientId)
      .then(res => {
        const preview: RenewalVehicle[] = Array.isArray(res.data) ? res.data : [];
        setRenewalVehicles(preview);
        setSelectedRenewalVehicleIds(preview.filter(v => v.isEligible && !v.isTemporaryCover).map(v => v.id));
      })
      .catch(() => setRenewalError('Unable to load the vehicle carry-forward review.'))
      .finally(() => setRenewalVehiclesLoading(false));
  };

  const reviewRenewal = () => {
    const start = londonInputToIso(renewalForm.policyStartDate);
    const end = londonInputToIso(renewalForm.policyEndDate);
    if (!start || !end || new Date(end) <= new Date(start)) {
      setRenewalError('Enter a policy end date/time after the policy start.');
      return;
    }
    setRenewalError(null);
    setRenewalReview(true);
  };

  const confirmRenewal = async () => {
    setRenewingPolicy(true);
    setRenewalError(null);
    try {
      await clientsAPI.renewPolicyPeriod(clientId, {
        ...renewalForm,
        policyStartDate: londonInputToIso(renewalForm.policyStartDate),
        policyEndDate: londonInputToIso(renewalForm.policyEndDate),
        selectedVehicleIds: selectedRenewalVehicleIds,
      });
      const refreshed = await clientsAPI.getById(clientId);
      setClientData(refreshed.data);
      const periods = await clientsAPI.getPolicyPeriods(clientId);
      setPolicyPeriods(Array.isArray(periods.data) ? periods.data : []);
      setShowRenewal(false);
      setRenewalReview(false);
      alert('The policy period has been renewed and the previous period snapshot has been archived.');
    } catch (err: any) {
      setRenewalError(err.response?.data?.message || 'Policy renewal failed. No changes were made.');
    } finally {
      setRenewingPolicy(false);
    }
  };
  // ─── Lazy loaders ─────────────────────────────────────────────────────────────
  const loadSites = async () => {
    setSitesLoading(true);
    try {
      const res = await sitesAPI.getByClient(clientId);
      setSites(res.data.filter((s: any) => s.isActive !== false));
      setSitesFetched(true);
    } catch (err) {
      console.error('Error loading sites', err);
    } finally {
      setSitesLoading(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await usersAPI.getAll();
      setUsers(res.data.filter((u: any) => u.clientId === clientId));
      setUsersFetched(true);
    } catch (err) {
      console.error('Error loading users', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadCoverExtras = async () => {
    // Load each independently so one failure doesn't prevent the other loading
    try {
      const videoRes = await clientsAPI.getLoginVideo(clientId);
      setLoginVideoFileName(videoRes.data.fileName || null);
    } catch (err) {
      console.error('Error loading login video', err);
    }
    try {
      const imgRes = await clientsAPI.getVehicleImage(clientId);
      setVehicleImageBase64(imgRes.data.vehicleImageBase64 || null);
    } catch {
      // vehicle image is optional
    }
    if (!usersFetched) await loadUsers();
    setCoverFetched(true);
  };

  const handleVehicleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVehicleImageError(null);
    setVehicleImageSuccess(false);
    const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) { setVehicleImageError('Only PNG, JPEG, GIF, WebP and SVG are supported.'); return; }
    if (file.size > 2 * 1024 * 1024) { setVehicleImageError('File must be under 2 MB.'); return; }
    setVehicleImageUploading(true);
    try {
      const res = await clientsAPI.uploadVehicleImage(clientId, file);
      setVehicleImageBase64(res.data.vehicleImageBase64);
      setVehicleImageSuccess(true);
      setTimeout(() => setVehicleImageSuccess(false), 3000);
    } catch (err: any) {
      setVehicleImageError(err.response?.data?.message || 'Upload failed.');
    } finally {
      setVehicleImageUploading(false);
      if (vehicleImageInputRef.current) vehicleImageInputRef.current.value = '';
    }
  };

  const handleVehicleImageDelete = async () => {
    if (!window.confirm('Remove the custom fleet vehicle image for this client?')) return;
    try {
      await clientsAPI.deleteVehicleImage(clientId);
      setVehicleImageBase64(null);
    } catch {
      setVehicleImageError('Failed to remove vehicle image.');
    }
  };

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    if (tab === 'sites' && !sitesFetched) loadSites();
    if (tab === 'users' && !usersFetched) loadUsers();
    if (tab === 'cover-settings' && !coverFetched) loadCoverExtras();
  };

  // ─── Logo ─────────────────────────────────────────────────────────────────────
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoError(null);
    setLogoSuccess(false);
    const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) { setLogoError('Only PNG, JPEG, GIF, WebP and SVG files are supported.'); return; }
    if (file.size > 2 * 1024 * 1024) { setLogoError('File must be under 2 MB.'); return; }
    setLogoUploading(true);
    try {
      const res = await clientsAPI.uploadLogo(clientId, file);
      setLogoBase64(res.data.logoBase64);
      setLogoSuccess(true);
      setTimeout(() => setLogoSuccess(false), 3000);
    } catch (err: any) {
      setLogoError(err.response?.data?.message || 'Upload failed.');
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Remove this client logo?')) return;
    try {
      await clientsAPI.deleteLogo(clientId);
      setLogoBase64(null);
    } catch {
      setLogoError('Failed to remove logo.');
    }
  };

  // ─── Insurer ──────────────────────────────────────────────────────────────────
  const handleSaveInsurer = async () => {
    setInsurerSaving(true);
    setInsurerError(null);
    try {
      await saveClientPatch({
        insurerName: insurerForm.insurerName || null,
        insurerMidtermEmail: insurerForm.insurerMidtermEmail || null,
        fleetAtlasContactEmail: insurerForm.fleetAtlasContactEmail || null,
      });
      setEditingInsurer(false);
    } catch {
      setInsurerError('Failed to save insurer details.');
    } finally {
      setInsurerSaving(false);
    }
  };

  // ─── Cover letter details ─────────────────────────────────────────────────────
  const handleSaveCoverLetter = async () => {
    setCoverLetterSaving(true);
    setCoverLetterError(null);
    try {
      await saveClientPatch(coverLetterForm);
      setEditingCoverLetter(false);
    } catch {
      setCoverLetterError('Failed to save cover letter details.');
    } finally {
      setCoverLetterSaving(false);
    }
  };

  // ─── Cover settings ───────────────────────────────────────────────────────────
  const handleSaveCoverSettings = async () => {
    setCoverSaving(true);
    setCoverError(null);
    try {
      await saveClientPatch({
        instantCoverEnabled, requiresVehicleCategory, showDriverPolicyBanner, trailersEnabled, claimsEnabled, mtaValueThreshold,
        claimsInboxEmail: claimsInboxEmail.trim() || null,
        claimsNotificationRecipientUserIds: claimsNotificationRecipientUserIds.join(',') || null,
      });
      setCoverSaved(true);
      setTimeout(() => setCoverSaved(false), 3000);
    } catch {
      setCoverError('Failed to save cover settings.');
    } finally {
      setCoverSaving(false);
    }
  };

  // ─── Named driver policy ──────────────────────────────────────────────────────
  const handleSavePolicy = async () => {
    setPolicySaving(true);
    setPolicyError(null);
    try {
      await saveClientPatch({ namedDriverPolicy: policyForm || null });
      setEditingPolicy(false);
    } catch {
      setPolicyError('Failed to save policy.');
    } finally {
      setPolicySaving(false);
    }
  };

  // ─── Login video
  const handleLoginVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoginVideoError(null);
    setLoginVideoSuccess(false);
    const allowed = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!allowed.includes(file.type)) { setLoginVideoError('Only MP4, WebM and MOV files are supported.'); return; }
    if (file.size > 100 * 1024 * 1024) { setLoginVideoError('File must be under 100 MB.'); return; }
    setLoginVideoUploading(true);
    try {
      const res = await clientsAPI.uploadLoginVideo(clientId, file);
      setLoginVideoFileName(res.data.fileName || null);
      setLoginVideoSuccess(true);
      setTimeout(() => setLoginVideoSuccess(false), 3000);
    } catch (err: any) {
      setLoginVideoError(err.response?.data?.message || 'Upload failed.');
    } finally {
      setLoginVideoUploading(false);
      if (loginVideoInputRef.current) loginVideoInputRef.current.value = '';
    }
  };

  const handleLoginVideoDelete = async () => {
    if (!window.confirm('Remove the login background video for this client?')) return;
    try {
      await clientsAPI.deleteLoginVideo(clientId);
      setLoginVideoFileName(null);
    } catch {
      setLoginVideoError('Failed to remove video.');
    }
  };

  // ─── Notes ────────────────────────────────────────────────────────────────────
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    const timestamp = new Date().toLocaleString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
    const noteWithTimestamp = `${newNote} (${timestamp})`;
    const updatedNotes = notes ? `${notes}\n${noteWithTimestamp}` : noteWithTimestamp;
    try {
      await saveClientPatch({ notes: updatedNotes });
      setNotes(updatedNotes);
      setNewNote('');
    } catch {
      alert('Failed to add note');
    }
  };

  // ─── Sites ────────────────────────────────────────────────────────────────────
  const handleAddSite = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await sitesAPI.create({ ...siteForm, clientId });
      setShowAddSite(false);
      setSiteForm({ name: '', addressLine1: '', city: '', postcode: '' });
      loadSites();
    } catch (err: any) {
      const msg = err?.response?.data?.message
        || err?.response?.data?.title
        || JSON.stringify(err?.response?.data)
        || 'Failed to create site';
      alert(`Failed to create site: ${msg}`);
    }
  };

  const handleDeleteSite = async (siteId: number, siteName: string) => {
    if (!window.confirm(`Delete site "${siteName}"?`)) return;
    try {
      await sitesAPI.delete(siteId);
      loadSites();
    } catch {
      alert('Failed to delete site');
    }
  };

  // ─── Users ────────────────────────────────────────────────────────────────────
  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    const username = userForm.username.trim();
    if (username.includes('@') || /\s/.test(username)) {
      alert('Username cannot be an email address or contain spaces. Use something like FirstLast (e.g. EdytaHaines).');
      return;
    }
    if (username.length < 3) {
      alert('Username must be at least 3 characters.');
      return;
    }
    try {
      await usersAPI.create({ ...userForm, username, clientId, role: 'Client' });
      setShowAddUser(false);
      setUserForm({ username: '', email: '', password: '', firstName: '', lastName: '' });
      loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to create user');
    }
  };

  const toggleUserStatus = async (userId: number, currentStatus: boolean) => {
    try {
      if (currentStatus) await usersAPI.deactivate(userId);
      else await usersAPI.activate(userId);
      loadUsers();
    } catch {
      alert('Failed to update user status');
    }
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────────
  const getCoverStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return '#28a745';
      case 'Cover Pending': return '#FFA500';
      case 'Inactive': return '#dc3545';
      default: return '#6c757d';
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  if (pageLoading) return <div className="cd-loading">Loading client…</div>;
  if (!clientData) return <div className="cd-loading">Client not found.</div>;

  return (
    <div className="client-details-page">

      {/* ── Page header ── */}
      <div className="cd-page-header">
        <button className="cd-back-btn" onClick={() => navigate('/clients')}>
          ← Back to Clients
        </button>
        <div className="cd-title-row">
          {logoBase64 && (
            <img src={logoBase64} alt="" className="cd-header-logo" />
          )}
          <h1>{clientData.companyName}</h1>
        </div>
      </div>

      {/* ── Overview cards ── */}
      <div className="cd-overview-grid">

        {/* Address */}
        <div className="cd-info-card">
          <div className="cd-card-label">Address</div>
          <div className="cd-card-body">
            <div>{clientData.addressLine1}</div>
            {clientData.addressLine2 && <div>{clientData.addressLine2}</div>}
            <div>{clientData.city}{clientData.county ? `, ${clientData.county}` : ''}</div>
            <div>{clientData.postcode}</div>
          </div>
        </div>

        {/* Contact */}
        <div className="cd-info-card">
          <div className="cd-card-label">Contact</div>
          <div className="cd-card-body">
            <div><strong>{clientData.contactPerson}</strong></div>
            <div><a href={`mailto:${clientData.contactEmail}`}>{clientData.contactEmail}</a></div>
            <div>{clientData.contactPhone}</div>
          </div>
        </div>

        {/* Policy and portal access schedule */}
        <div className="cd-info-card" style={{ gridColumn: 'span 2', borderColor: '#93c5fd', background: '#f8fbff' }}>
          <div className="cd-card-label-row">
            <div className="cd-card-label" style={{ color: '#1d4ed8' }}>Policy &amp; Myshield Access Schedule</div>
            {!editingSchedule && canEditOnboarding && (
              <button className="btn btn-secondary btn-sm" onClick={startScheduleEdit}>✏️ Edit schedule</button>
            )}
          </div>
          {!editingSchedule ? (
            <div className="cd-card-body">
              <div><span className="cd-field-label">Policy starts:</span> {formatLondonDateTime(clientData.policyStartDate) || <span className="cd-muted">Not scheduled</span>}</div>
              <div><span className="cd-field-label">Policy ends:</span> {formatLondonDateTime(clientData.policyEndDate) || <span className="cd-muted">Not scheduled</span>}</div>
              <div style={{ borderTop: '1px solid #dbeafe', marginTop: 8, paddingTop: 8 }}>
                <div><span className="cd-field-label">Myshield access begins:</span> {formatLondonDateTime(clientData.myshieldAccessStartDate) || <span className="cd-muted">Available immediately</span>}</div>
                <div><span className="cd-field-label">Myshield access ends:</span> {formatLondonDateTime(clientData.myshieldAccessEndDate) || <span className="cd-muted">No scheduled end</span>}</div>
              </div>
              <div className="cd-hint">Portal scheduling applies to Client and Fleet Manager users only. Admin and MTA access remains available.</div>
              {isSuperAdmin && <div className="cd-btn-row"><button className="btn btn-primary btn-sm" onClick={startRenewal}>↻ Renew / Start Next Policy Period</button></div>}
            </div>
          ) : (
            <div className="cd-insurer-edit">
              <label><span className="cd-field-label">Policy start (UK time — GMT/BST)</span><input type="datetime-local" value={scheduleForm.policyStartDate} onChange={e => setScheduleForm({ ...scheduleForm, policyStartDate: e.target.value })} /></label>
              <label><span className="cd-field-label">Policy end (UK time — GMT/BST)</span><input type="datetime-local" value={scheduleForm.policyEndDate} onChange={e => setScheduleForm({ ...scheduleForm, policyEndDate: e.target.value })} /></label>
              <label><span className="cd-field-label">Myshield access start (UK time — GMT/BST)</span><input type="datetime-local" value={scheduleForm.myshieldAccessStartDate} onChange={e => setScheduleForm({ ...scheduleForm, myshieldAccessStartDate: e.target.value })} /></label>
              <label><span className="cd-field-label">Myshield access end (UK time — GMT/BST)</span><input type="datetime-local" value={scheduleForm.myshieldAccessEndDate} onChange={e => setScheduleForm({ ...scheduleForm, myshieldAccessEndDate: e.target.value })} /></label>
              <div className="cd-hint">Leave a field blank when no limit is required. UK daylight-saving changes are handled automatically.</div>
              {scheduleError && <div className="cd-error">{scheduleError}</div>}
              <div className="cd-btn-row">
                <button className="btn btn-primary btn-sm" onClick={handleSaveSchedule} disabled={scheduleSaving}>{scheduleSaving ? 'Saving…' : 'Save schedule'}</button>
                <button className="btn btn-secondary btn-sm" onClick={() => { setEditingSchedule(false); setScheduleError(null); }}>Cancel</button>
              </div>
            </div>
          )}
        </div>

        {/* Insurer */}
        <div className="cd-info-card cd-insurer-card">
          <div className="cd-card-label-row">
            <div className="cd-card-label">Insurer &amp; Contact</div>
          {!editingInsurer && canEditOnboarding && (
              <button className="btn btn-secondary btn-sm" onClick={() => setEditingInsurer(true)}>✏️ Edit</button>
            )}
          </div>
          {!editingInsurer ? (
            <div className="cd-card-body">
              <div><span className="cd-field-label">Insurer:</span> {clientData.insurerName || <span className="cd-muted">Not set</span>}</div>
              <div>
                <span className="cd-field-label">Midterm email:</span>{' '}
                {clientData.insurerMidtermEmail
                  ? <a href={`mailto:${clientData.insurerMidtermEmail}`}>{clientData.insurerMidtermEmail}</a>
                  : <span className="cd-muted">Not set</span>}
              </div>
              <div>
                <span className="cd-field-label">Myshield contact:</span>{' '}
                {clientData.fleetAtlasContactEmail
                  ? <a href={`mailto:${clientData.fleetAtlasContactEmail}`}>{clientData.fleetAtlasContactEmail}</a>
                  : <span className="cd-muted">Not set</span>}
              </div>
            </div>
          ) : (
            <div className="cd-insurer-edit">
              <input
                type="text"
                placeholder="Insurer name"
                value={insurerForm.insurerName}
                onChange={e => setInsurerForm({ ...insurerForm, insurerName: e.target.value })}
              />
              <input
                type="email"
                placeholder="Midterm team email"
                value={insurerForm.insurerMidtermEmail}
                onChange={e => setInsurerForm({ ...insurerForm, insurerMidtermEmail: e.target.value })}
              />
              <input
                type="email"
                placeholder="Myshield contact email"
                value={insurerForm.fleetAtlasContactEmail}
                onChange={e => setInsurerForm({ ...insurerForm, fleetAtlasContactEmail: e.target.value })}
              />
              {insurerError && <div className="cd-error">{insurerError}</div>}
              <div className="cd-btn-row">
                <button className="btn btn-primary btn-sm" onClick={handleSaveInsurer} disabled={insurerSaving}>
                  {insurerSaving ? 'Saving…' : 'Save'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => { setEditingInsurer(false); setInsurerError(null); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Logo */}
        <div className="cd-info-card">
          <div className="cd-card-label">Client Logo</div>
          <div className="cd-card-body">
            {logoBase64 ? (
              <div className="cd-logo-section">
                <img src={logoBase64} alt="Logo" className="cd-logo-preview" />
                {canEditOnboarding && (
                  <div className="cd-btn-row">
                    <button className="btn btn-secondary btn-sm" onClick={() => logoInputRef.current?.click()} disabled={logoUploading}>
                      {logoUploading ? 'Uploading…' : 'Replace'}
                    </button>
                    <button className="cd-remove-btn" onClick={handleLogoDelete}>Remove</button>
                  </div>
                )}
              </div>
            ) : (
              <div className="cd-logo-empty">
                <span className="cd-muted">No logo uploaded.</span>
                {canEditOnboarding && (
                  <button className="btn btn-primary btn-sm" onClick={() => logoInputRef.current?.click()} disabled={logoUploading}>
                    {logoUploading ? 'Uploading…' : 'Upload Logo'}
                  </button>
                )}
              </div>
            )}
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
              style={{ display: 'none' }}
              onChange={handleLogoUpload}
            />
            {logoError && <div className="cd-error">{logoError}</div>}
            {logoSuccess && <div className="cd-success">✓ Logo updated.</div>}
            <div className="cd-hint">PNG, JPEG, GIF, WebP or SVG · Max 2 MB</div>
          </div>
        </div>

        {canViewPolicyArchives && (
          <div className="cd-info-card" style={{ gridColumn: 'span 2', borderColor: '#c4b5fd', background: '#fafafa' }}>
            <div className="cd-card-label" style={{ color: '#5b21b6' }}>Policy Period History</div>
            <div className="cd-card-body">
              {periodsLoading ? <span className="cd-muted">Loading policy periods…</span> : policyPeriods.length === 0 ? <span className="cd-muted">No policy periods have been recorded for this client.</span> : policyPeriods.map(period => (
                <div key={period.id} style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e5e7eb' }}>
                  <div><strong>Policy Period {period.periodNumber}</strong><div className="cd-hint">{formatLondonDateTime(period.policyStartDate)} – {formatLondonDateTime(period.policyEndDate)} · {period.status === 'Archived' ? `Archived ${formatLondonDateTime(period.archivedAt) || ''}` : 'Current live policy period'}</div></div>
                  {period.status === 'Archived' && <button className="btn btn-secondary btn-sm" onClick={() => viewArchiveSnapshot(period)}>View snapshot</button>}
                </div>
              ))}
              {archiveError && <div className="cd-error">{archiveError}</div>}
            </div>
          </div>
        )}
      </div>{/* end overview grid */}

      {showRenewal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2>{renewalReview ? 'Confirm Policy Renewal' : 'Start Next Policy Period'}</h2><button className="close-btn" onClick={() => !renewingPolicy && setShowRenewal(false)}>✕</button></div>
            <div style={{ padding: 24 }}>
              {!renewalReview ? <><p style={{ marginTop: 0, color: '#4b5563' }}>This archives the current policy period as a read-only snapshot and makes the dates below the new live period.</p>
                <div className="cd-insurer-edit">
                  <label><span className="cd-field-label">New policy start (UK time — GMT/BST)</span><input type="datetime-local" value={renewalForm.policyStartDate} onChange={e => setRenewalForm({ ...renewalForm, policyStartDate: e.target.value })} /></label>
                  <label><span className="cd-field-label">New policy end (UK time — GMT/BST)</span><input type="datetime-local" value={renewalForm.policyEndDate} onChange={e => setRenewalForm({ ...renewalForm, policyEndDate: e.target.value })} /></label>
                  <label><span className="cd-field-label">Policy reference</span><input value={renewalForm.policyReference} onChange={e => setRenewalForm({ ...renewalForm, policyReference: e.target.value })} /></label>
                  <label><span className="cd-field-label">Insurer</span><input value={renewalForm.insurerName} onChange={e => setRenewalForm({ ...renewalForm, insurerName: e.target.value })} /></label>
                  <label><span className="cd-field-label">Cover type</span><input value={renewalForm.coverType} onChange={e => setRenewalForm({ ...renewalForm, coverType: e.target.value })} /></label>
                </div>
                <div style={{ marginTop: 18 }}><strong>Current live vehicles entering the next policy period ({selectedRenewalVehicleIds.length} selected)</strong><p className="cd-hint">Temporary vehicles are shown first and are not selected automatically. This review includes only vehicles currently on cover.</p>
                  {renewalVehiclesLoading ? <div className="cd-muted">Loading vehicle review…</div> : Object.entries(renewalVehicles.reduce((groups: Record<string, RenewalVehicle[]>, vehicle) => { (groups[vehicle.siteName] ||= []).push(vehicle); return groups; }, {})).map(([siteName, siteVehicles]) => (
                    <div key={siteName} style={{ marginTop: 12, border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}><div style={{ background: '#f8fafc', padding: '7px 10px', fontWeight: 700 }}>{siteName}</div>
                      <div style={{ overflowX: 'auto' }}><table style={{ fontSize: 12 }}><thead><tr><th>Select</th><th>Registration</th><th>Vehicle</th><th>Cover</th></tr></thead><tbody>{siteVehicles.map(vehicle => <tr key={vehicle.id} style={{ background: vehicle.isTemporaryCover ? '#fff7ed' : undefined }}><td><input type="checkbox" disabled={!vehicle.isEligible} checked={selectedRenewalVehicleIds.includes(vehicle.id)} onChange={e => setSelectedRenewalVehicleIds(ids => e.target.checked ? [...ids, vehicle.id] : ids.filter(id => id !== vehicle.id))} /></td><td><strong>{vehicle.registration}</strong></td><td>{vehicle.make} {vehicle.model}</td><td><strong style={{ color: vehicle.isTemporaryCover ? '#c2410c' : '#15803d' }}>{vehicle.isTemporaryCover ? 'Temporary' : 'Permanent'}</strong></td></tr>)}</tbody></table></div>
                    </div>
                  ))}
                </div>
                {renewalError && <div className="cd-error">{renewalError}</div>}
                <div className="cd-btn-row"><button className="btn btn-primary" onClick={reviewRenewal}>Review renewal</button><button className="btn btn-secondary" onClick={() => setShowRenewal(false)}>Cancel</button></div>
              </> : <><div style={{ padding: 16, background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8 }}><strong>Final confirmation</strong><br/>Archive the current policy period and activate the next period for {clientData.companyName}. This cannot be undone.</div>
                <div className="cd-card-body" style={{ marginTop: 16 }}><div><span className="cd-field-label">Outgoing period:</span> {formatLondonDateTime(clientData.policyStartDate) || 'Not scheduled'} → {formatLondonDateTime(clientData.policyEndDate) || 'Not scheduled'}</div><div><span className="cd-field-label">Current vehicle totals:</span> {vehicles.filter(v => v.coverStatus === 'Active').length} live / {vehicles.filter(v => v.coverStatus === 'Cover Pending').length} pending</div><div><span className="cd-field-label">Selected carry-forward vehicles:</span> <strong>{selectedRenewalVehicleIds.length}</strong></div><div><span className="cd-field-label">New period:</span> <strong>{renewalForm.policyStartDate} → {renewalForm.policyEndDate} UK time</strong></div></div>
                {renewalError && <div className="cd-error">{renewalError}</div>}
                <div className="cd-btn-row"><button className="btn btn-primary" onClick={confirmRenewal} disabled={renewingPolicy}>{renewingPolicy ? 'Renewing…' : 'Confirm and archive current period'}</button><button className="btn btn-secondary" onClick={() => setRenewalReview(false)} disabled={renewingPolicy}>Back</button></div>
              </>}
            </div>
          </div>
        </div>
      )}

      {archiveSnapshot && (() => {
        const period = archiveSnapshot.period || {};
        const periodNumber = period.periodNumber ?? period.PeriodNumber;
        const vehicles = (archiveSnapshot.vehicles || []).map((vehicle: any) => ({
          id: vehicle.id ?? vehicle.Id,
          registration: vehicle.registration ?? vehicle.Registration ?? '',
          make: vehicle.make ?? vehicle.Make ?? '',
          model: vehicle.model ?? vehicle.Model ?? '',
          vehicleValue: vehicle.vehicleValue ?? vehicle.VehicleValue,
          coverStatus: vehicle.coverStatus ?? vehicle.CoverStatus ?? '—',
          inceptionDate: vehicle.inceptionDate ?? vehicle.InceptionDate ?? null,
        }));
        return (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 980 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2>Archived Policy Period {periodNumber}</h2><button className="close-btn" onClick={() => setArchiveSnapshot(null)}>✕</button></div>
            <div style={{ padding: 24 }}><p style={{ marginTop: 0, color: '#6b7280' }}>Read-only evidence snapshot taken when this policy period was archived.</p>
              {vehicles.length === 0 ? (
                <p className="cd-muted">No vehicles were captured in this snapshot.</p>
              ) : (
              <div style={{ overflowX: 'auto' }}><table><thead><tr><th>Registration</th><th>Make / Model</th><th>Value</th><th>Cover status</th><th>Inception</th></tr></thead><tbody>{vehicles.map((vehicle: any) => <tr key={vehicle.id}><td><strong>{vehicle.registration || '—'}</strong></td><td>{[vehicle.make, vehicle.model].filter(Boolean).join(' ') || '—'}</td><td>{vehicle.vehicleValue ? `£${Number(vehicle.vehicleValue).toLocaleString()}` : '—'}</td><td>{vehicle.coverStatus}</td><td>{vehicle.inceptionDate ? formatDateBritish(vehicle.inceptionDate) : '—'}</td></tr>)}</tbody></table></div>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── Tabs ── */}
      <div className="cd-tabs-container">
        <div className="cd-tabs">
          {([
            { key: 'vehicles',      icon: vehicleIcon,   label: 'Vehicles',       count: vehiclesFetched ? vehicles.length : null },
            { key: 'sites',         icon: sitesIcon,     label: 'Sites',          count: sitesFetched ? sites.length : null },
            { key: 'users',         icon: usersIcon,     label: 'Users',          count: usersFetched ? users.length : null },
            { key: 'cover-settings',icon: insuranceIcon, label: 'Cover Settings', count: null },
            { key: 'notes',         icon: notesIcon,     label: 'Notes',          count: null },
          ] as const).map(tab => (
            <button
              key={tab.key}
              className={`cd-tab${activeTab === tab.key ? ' active' : ''}`}
              onClick={() => handleTabChange(tab.key)}
            >
              <img src={tab.icon} alt="" style={{ width: 15, height: 15, marginRight: 6, verticalAlign: 'middle' }} />
              {tab.label}{tab.count !== null ? ` (${tab.count})` : ''}
            </button>
          ))}
        </div>

        <div className="cd-tab-content">

          {/* ── Vehicles ── */}
          {activeTab === 'vehicles' && (
            vehiclesLoading ? (
              <div className="cd-tab-loading">Loading vehicles…</div>
            ) : vehicles.length === 0 ? (
              <p className="cd-empty">No active vehicles found for this client.</p>
            ) : (
              <table className="cd-table">
                <thead>
                  <tr>
                    <th>Registration</th>
                    <th>Make &amp; Model</th>
                    <th>Year</th>
                    <th>Value</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map(v => (
                    <tr key={v.id}>
                      <td><strong>{v.registration}</strong></td>
                      <td>{v.make} {v.model}</td>
                      <td>{v.year}</td>
                      <td>£{v.vehicleValue?.toLocaleString() || '0'}</td>
                      <td>
                        <span style={{ color: isAwaitingInception(v) ? '#7c3aed' : getCoverStatusColor(v.coverStatus), fontWeight: 600, fontSize: '13px' }}>
                          {isAwaitingInception(v) ? 'Awaiting Inception' : v.coverStatus === 'Active' ? 'On Cover' : v.coverStatus}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}

          {/* ── Sites ── */}
          {activeTab === 'sites' && (
            <div>
              <div className="cd-tab-header">
                <h3>Sites &amp; Locations</h3>
                {canEditOnboarding && <button className="btn btn-primary btn-sm" onClick={() => setShowAddSite(true)}>➕ Add Site</button>}
              </div>
              {showAddSite && canEditOnboarding && (
                <form onSubmit={handleAddSite} className="cd-inline-form">
                  <div className="cd-form-row">
                    <input placeholder="Site Name *" value={siteForm.name} onChange={e => setSiteForm({ ...siteForm, name: e.target.value })} required />
                    <input placeholder="Address *" value={siteForm.addressLine1} onChange={e => setSiteForm({ ...siteForm, addressLine1: e.target.value })} required />
                  </div>
                  <div className="cd-form-row">
                    <input placeholder="City *" value={siteForm.city} onChange={e => setSiteForm({ ...siteForm, city: e.target.value })} required />
                    <input placeholder="Postcode *" value={siteForm.postcode} onChange={e => setSiteForm({ ...siteForm, postcode: e.target.value })} required />
                  </div>
                  <div className="cd-form-actions">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowAddSite(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary btn-sm">Save Site</button>
                  </div>
                </form>
              )}
              {sitesLoading ? (
                <div className="cd-tab-loading">Loading sites…</div>
              ) : sites.length === 0 ? (
                <p className="cd-empty">No sites configured.</p>
              ) : (
                <div className="cd-sites-grid">
                  {sites.map(s => (
                    <div key={s.id} className="cd-site-card">
                      {canEditOnboarding && <button className="cd-site-delete" onClick={() => handleDeleteSite(s.id, s.name)} title="Delete site">🗑️</button>}
                      <h4>{s.name}</h4>
                      <p>{s.addressLine1}</p>
                      <p>{s.city}, {s.postcode}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Users ── */}
          {activeTab === 'users' && (
            <div>
              <div className="cd-tab-header">
                <h3>Client Users</h3>
                {canEditOnboarding && <button className="btn btn-primary btn-sm" onClick={() => setShowAddUser(true)}>➕ Add User</button>}
              </div>
              {showAddUser && canEditOnboarding && (
                <form onSubmit={handleAddUser} className="cd-inline-form">
                  <div className="cd-form-row">
                    <input
                      placeholder="Username * (not email)"
                      value={userForm.username}
                      onChange={e => setUserForm({ ...userForm, username: e.target.value.replace(/\s/g, '') })}
                      required
                    />
                    <input type="email" placeholder="Email *" value={userForm.email} onChange={e => setUserForm({ ...userForm, email: e.target.value })} required />
                  </div>
                  <div className="cd-form-row">
                    <input placeholder="First Name *" value={userForm.firstName} onChange={e => setUserForm({ ...userForm, firstName: e.target.value })} required />
                    <input placeholder="Last Name *" value={userForm.lastName} onChange={e => setUserForm({ ...userForm, lastName: e.target.value })} required />
                  </div>
                  <input
                    type="password"
                    placeholder="Password *"
                    value={userForm.password}
                    onChange={e => setUserForm({ ...userForm, password: e.target.value })}
                    required
                    minLength={6}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e0', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '10px' }}
                  />
                  <div className="cd-form-actions">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowAddUser(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary btn-sm">Create User</button>
                  </div>
                </form>
              )}
              {usersLoading ? (
                <div className="cd-tab-loading">Loading users…</div>
              ) : users.length === 0 ? (
                <p className="cd-empty">No users found. Add users to grant Environment access to this client.</p>
              ) : (
                <table className="cd-table">
                  <thead>
                    <tr>
                      <th>Name</th><th>Username</th><th>Email</th><th>Status</th>
                      {canEditOnboarding && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id}>
                        <td><strong>{u.firstName} {u.lastName}</strong></td>
                        <td>{u.username}</td>
                        <td>{u.email}</td>
                        <td>
                          <span style={{ color: u.isActive ? '#28a745' : '#dc3545', fontWeight: 600, fontSize: '13px' }}>
                            {u.isActive ? '✓ Active' : '✖ Inactive'}
                          </span>
                        </td>
                        {canEditOnboarding && (
                          <td>
                            <button
                              onClick={() => toggleUserStatus(u.id, u.isActive)}
                              style={{
                                backgroundColor: u.isActive ? '#dc3545' : '#28a745',
                                color: 'white', border: 'none', padding: '4px 12px',
                                borderRadius: '50px', cursor: 'pointer', fontSize: '12px', fontWeight: 500
                              }}
                            >
                              {u.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ── Cover Settings ── */}
          {activeTab === 'cover-settings' && (
            <div className="cd-cover-settings">

              {/* Instant Cover */}
              <div className="cd-settings-block">
                <h3>⚡ Instant Cover</h3>
                <p className="cd-settings-desc">
                  When enabled, vehicles submitted by this client go straight to <strong>On Cover</strong> status rather than <strong>Pending Cover</strong>.
                  This applies to individual submissions and bulk CSV uploads.
                </p>
                <div className="cd-toggle-row">
                  <label style={{ position: 'relative', display: 'inline-block', width: '52px', height: '28px', flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={instantCoverEnabled}
                      onChange={e => setInstantCoverEnabled(e.target.checked)}
                      style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                    />
                    <span style={{
                      position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                      backgroundColor: instantCoverEnabled ? '#2563eb' : '#cbd5e0',
                      borderRadius: '28px', transition: 'background-color 0.2s'
                    }}>
                      <span style={{
                        position: 'absolute', height: '20px', width: '20px',
                        left: instantCoverEnabled ? '28px' : '4px', bottom: '4px',
                        backgroundColor: 'white', borderRadius: '50%', transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                      }} />
                    </span>
                  </label>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '15px', color: instantCoverEnabled ? '#1d4ed8' : '#4a5568' }}>
                      {instantCoverEnabled ? '✓ Instant Cover Enabled' : 'Instant Cover Disabled'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#718096', marginTop: '2px' }}>
                      {instantCoverEnabled
                        ? 'New vehicles will be placed On Cover immediately upon submission.'
                        : 'New vehicles will require manual cover approval (Pending Cover).'}
                    </div>
                  </div>
                </div>
                {/* Vehicle Category toggle */}
                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 18, marginTop: 18 }}>
                  <h4 style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 700, color: '#374151' }}>📋 Vehicle Form Fields</h4>
                  <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>
                    When enabled, this client must select a <strong>Vehicle Category</strong> (TAV or Contract Hire) each time
                    they add a vehicle or a temporary vehicle. Other clients are not affected.
                  </p>
                  <div className="cd-toggle-row">
                    <label style={{ position: 'relative', display: 'inline-block', width: '52px', height: '28px', flexShrink: 0 }}>
                      <input
                        type="checkbox"
                        checked={requiresVehicleCategory}
                        onChange={e => setRequiresVehicleCategory(e.target.checked)}
                        style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                      />
                      <span style={{
                        position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: requiresVehicleCategory ? '#2563eb' : '#cbd5e0',
                        borderRadius: '28px', transition: 'background-color 0.2s'
                      }}>
                        <span style={{
                          position: 'absolute', height: '20px', width: '20px',
                          left: requiresVehicleCategory ? '28px' : '4px', bottom: '4px',
                          backgroundColor: 'white', borderRadius: '50%', transition: 'left 0.2s',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                        }} />
                      </span>
                    </label>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '15px', color: requiresVehicleCategory ? '#1d4ed8' : '#4a5568' }}>
                        {requiresVehicleCategory ? '✓ Vehicle Category Required' : 'Vehicle Category Not Required'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#718096', marginTop: '2px' }}>
                        {requiresVehicleCategory
                          ? 'Client must select TAV or Contract Hire when adding any vehicle.'
                          : 'Standard vehicle form — no category selection required.'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Driver Policy Banner toggle */}
                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 18, marginTop: 18 }}>
                  <h4 style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 700, color: '#374151' }}>📋 Named Driver Policy Banner</h4>
                  <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>
                    Controls whether the <strong>Named Driver &amp; Approved Driver Criteria</strong> information panel
                    is displayed at the top of the Referred Named Drivers page for this client.
                  </p>
                  <div className="cd-toggle-row">
                    <label style={{ position: 'relative', display: 'inline-block', width: '52px', height: '28px', flexShrink: 0 }}>
                      <input
                        type="checkbox"
                        checked={showDriverPolicyBanner}
                        onChange={e => setShowDriverPolicyBanner(e.target.checked)}
                        style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                      />
                      <span style={{
                        position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: showDriverPolicyBanner ? '#2563eb' : '#cbd5e0',
                        borderRadius: '28px', transition: 'background-color 0.2s'
                      }}>
                        <span style={{
                          position: 'absolute', height: '20px', width: '20px',
                          left: showDriverPolicyBanner ? '28px' : '4px', bottom: '4px',
                          backgroundColor: 'white', borderRadius: '50%', transition: 'left 0.2s',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                        }} />
                      </span>
                    </label>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '15px', color: showDriverPolicyBanner ? '#1d4ed8' : '#4a5568' }}>
                        {showDriverPolicyBanner ? '✓ Policy Banner Shown' : 'Policy Banner Hidden'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#718096', marginTop: '2px' }}>
                        {showDriverPolicyBanner
                          ? 'The driver criteria panel is visible to this client on the Named Drivers page.'
                          : 'The driver criteria panel is hidden for this client.'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Motor Claims toggle */}
                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 18, marginTop: 18 }}>
                  <h4 style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 700, color: '#374151' }}>📋 Motor Claims</h4>
                  <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>
                    When enabled, this client can register and track motor claims. Claims remain unavailable everywhere in the portal until this setting is saved.
                  </p>
                  <div className="cd-toggle-row">
                    <label style={{ position: 'relative', display: 'inline-block', width: '52px', height: '28px', flexShrink: 0 }}>
                      <input type="checkbox" checked={claimsEnabled} onChange={e => setClaimsEnabled(e.target.checked)}
                        style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
                      <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: claimsEnabled ? '#2563eb' : '#cbd5e0', borderRadius: '28px', transition: 'background-color 0.2s' }}>
                        <span style={{ position: 'absolute', height: '20px', width: '20px', left: claimsEnabled ? '28px' : '4px', bottom: '4px', backgroundColor: 'white', borderRadius: '50%', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                      </span>
                    </label>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '15px', color: claimsEnabled ? '#1d4ed8' : '#4a5568' }}>
                        {claimsEnabled ? '✓ Motor Claims Enabled' : 'Motor Claims Disabled'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#718096', marginTop: '2px' }}>
                        {claimsEnabled ? 'Claims is visible only to this client and authorised claims staff.' : 'Claims navigation, records and submissions are unavailable for this client.'}
                      </div>
                    </div>
                  </div>
                  {claimsEnabled && (
                    <div style={{ marginTop: 18, padding: 16, border: '1px solid #bfdbfe', borderRadius: 8, background: '#f8fbff' }}>
                      <h5 style={{ margin: '0 0 8px', color: '#1e3a8a', fontSize: 14 }}>Claims notice recipients</h5>
                      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#475569' }}>
                        Claim submissions and portal updates are copied to this Yourshield inbox and the selected active client users. If no client users are selected, the main client contact receives the notices.
                      </p>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>
                        Yourshield claims inbox email
                        <input
                          type="email"
                          value={claimsInboxEmail}
                          onChange={e => setClaimsInboxEmail(e.target.value)}
                          placeholder="claims@yourshield.co.uk"
                          style={{ display: 'block', width: '100%', maxWidth: 420, marginTop: 6, padding: '8px 10px', border: '1px solid #cbd5e0', borderRadius: 6, boxSizing: 'border-box' }}
                        />
                      </label>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151' }}>
                        Client users to receive notices
                        <select
                          multiple
                          size={Math.min(Math.max(users.filter(user => user.isActive).length, 3), 6)}
                          value={claimsNotificationRecipientUserIds}
                          onChange={e => setClaimsNotificationRecipientUserIds(Array.from(e.currentTarget.selectedOptions, option => option.value))}
                          style={{ display: 'block', width: '100%', maxWidth: 620, marginTop: 6, padding: 6, border: '1px solid #cbd5e0', borderRadius: 6 }}
                        >
                          {users.filter(user => user.isActive).map(user => (
                            <option key={user.id} value={String(user.id)}>{user.firstName} {user.lastName} — {user.email}</option>
                          ))}
                        </select>
                      </label>
                      <small style={{ display: 'block', marginTop: 8, color: '#64748b' }}>
                        Hold Ctrl (or Cmd on Mac) to select more than one user.
                        {claimsNotificationRecipientUserIds.length > 0 && ` Selected: ${users.filter(user => claimsNotificationRecipientUserIds.includes(String(user.id))).map(user => user.email).join(', ')}`}
                      </small>
                    </div>
                  )}
                </div>

                {/* Trailer Cover toggle */}
                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 18, marginTop: 18 }}>
                  <h4 style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 700, color: '#374151' }}>🚛 Trailer Cover</h4>
                  <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>
                    When enabled, this client can access the <strong>Trailers</strong> register under Vehicles to declare trailers requiring motor fleet cover.
                  </p>
                  <div className="cd-toggle-row">
                    <label style={{ position: 'relative', display: 'inline-block', width: '52px', height: '28px', flexShrink: 0 }}>
                      <input
                        type="checkbox"
                        checked={trailersEnabled}
                        onChange={e => setTrailersEnabled(e.target.checked)}
                        style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                      />
                      <span style={{
                        position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: trailersEnabled ? '#2563eb' : '#cbd5e0',
                        borderRadius: '28px', transition: 'background-color 0.2s'
                      }}>
                        <span style={{
                          position: 'absolute', height: '20px', width: '20px',
                          left: trailersEnabled ? '28px' : '4px', bottom: '4px',
                          backgroundColor: 'white', borderRadius: '50%', transition: 'left 0.2s',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                        }} />
                      </span>
                    </label>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '15px', color: trailersEnabled ? '#1d4ed8' : '#4a5568' }}>
                        {trailersEnabled ? '✓ Trailer Register Enabled' : 'Trailer Register Disabled'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#718096', marginTop: '2px' }}>
                        {trailersEnabled
                          ? 'The Trailers child item is visible under Vehicles for this client.'
                          : 'The Trailers register is hidden and cannot be accessed for this client.'}
                      </div>
                    </div>
                  </div>
                </div>
                {/* MTA Value Threshold */}
                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 18, marginTop: 18 }}>
                  <h4 style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 700, color: '#374151' }}>💰 MTA Value Threshold</h4>
                  <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>
                    When set, any vehicle with a declared value at or above this amount will be flagged with a <strong>High Value</strong> indicator
                    in the MTA workstream, and the client will see a referral warning when adding vehicles. Leave blank to disable the flag for this client.
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: '#374151' }}>£</span>
                    <input
                      type="number"
                      min={0}
                      step={1000}
                      placeholder="e.g. 100000"
                      value={mtaValueThreshold ?? ''}
                      onChange={e => setMtaValueThreshold(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                      style={{ padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 14, width: 180 }}
                    />
                    {mtaValueThreshold != null && (
                      <span style={{ fontSize: 13, color: '#6b7280' }}>
                        = £{mtaValueThreshold.toLocaleString()}
                      </span>
                    )}
                    {mtaValueThreshold != null && (
                      <button
                        type="button"
                        onClick={() => setMtaValueThreshold(null)}
                        style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 13, padding: '2px 4px' }}
                        title="Clear threshold"
                      >
                        ✕ Clear
                      </button>
                    )}
                  </div>
                  <small style={{ color: '#6b7280', fontSize: 12, marginTop: 6, display: 'block' }}>
                    This threshold is client-specific — different insurers may have different referral thresholds.
                  </small>
                </div>

                {coverError && <div className="cd-error">{coverError}</div>}
                {coverSaved && <div className="cd-success">✓ Cover settings saved.</div>}
                {canEditOnboarding && (
                  <button className="btn btn-primary btn-sm" onClick={handleSaveCoverSettings} disabled={coverSaving}>
                    {coverSaving ? 'Saving…' : 'Save Cover Settings'}
                  </button>
                )}
              </div>

              {/* Cover Letter Details */}
              <div className="cd-settings-block">
                <div className="cd-block-header">
                  <h3>📄 Cover Letter Details</h3>
                  {!editingCoverLetter && canEditOnboarding && (
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditingCoverLetter(true)}>✏️ Edit</button>
                  )}
                </div>
                <p className="cd-settings-desc">
                  These details appear on the PDF cover letter sent to the client when a vehicle action is approved.
                </p>
                {!editingCoverLetter ? (
                  <div className="cd-card-body">
                    <div><span className="cd-field-label">Policy Reference:</span> {clientData.policyReference || <span className="cd-muted">Not set</span>}</div>
                    <div><span className="cd-field-label">Business Description:</span> {clientData.businessDescription || <span className="cd-muted">Not set</span>}</div>
                    <div><span className="cd-field-label">Cover Type:</span> {clientData.coverType || <span className="cd-muted">Not set (defaults to Comprehensive / TPFT / TPO)</span>}</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <input
                        type="text"
                        placeholder="Policy reference (e.g. FLT/2025/001234)"
                        value={coverLetterForm.policyReference}
                        onChange={e => setCoverLetterForm({ ...coverLetterForm, policyReference: e.target.value })}
                        style={{ padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 14 }}
                      />
                      <input
                        type="text"
                        placeholder="Business description (e.g. Road Haulage)"
                        value={coverLetterForm.businessDescription}
                        onChange={e => setCoverLetterForm({ ...coverLetterForm, businessDescription: e.target.value })}
                        style={{ padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 14 }}
                      />
                      <select
                        value={coverLetterForm.coverType}
                        onChange={e => setCoverLetterForm({ ...coverLetterForm, coverType: e.target.value })}
                        style={{ padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 14, background: 'white' }}
                      >
                        <option value="">Cover type (leave blank for default)</option>
                        <option value="Comprehensive">Comprehensive</option>
                        <option value="Third Party Fire &amp; Theft">Third Party Fire &amp; Theft</option>
                        <option value="Third Party Only">Third Party Only</option>
                        <option value="Comprehensive / Third Party Fire &amp; Theft / Third Party Only">All types listed</option>
                      </select>
                    </div>
                    {coverLetterError && <div className="cd-error" style={{ marginTop: 8 }}>{coverLetterError}</div>}
                    <div className="cd-btn-row" style={{ marginTop: 12 }}>
                      <button className="btn btn-primary btn-sm" onClick={handleSaveCoverLetter} disabled={coverLetterSaving}>
                        {coverLetterSaving ? 'Saving…' : 'Save'}
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => {
                        setEditingCoverLetter(false);
                        setCoverLetterError(null);
                        setCoverLetterForm({ policyReference: clientData.policyReference || '', businessDescription: clientData.businessDescription || '', coverType: clientData.coverType || '' });
                      }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Named Driver Policy */}
              <div className="cd-settings-block cd-policy-block">
                <div className="cd-block-header">
                  <h3>📋 Named Driver &amp; Approved Driver Policy</h3>
                  {!editingPolicy && canEditOnboarding && (
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditingPolicy(true)}>✏️ Edit</button>
                  )}
                </div>
                {!editingPolicy ? (
                  clientData.namedDriverPolicy ? (
                    <p className="cd-policy-text">{clientData.namedDriverPolicy}</p>
                  ) : (
                    <p className="cd-muted">No named driver policy set. Click Edit to add the approved driver criteria for this client's fleet insurance.</p>
                  )
                ) : (
                  <div>
                    <textarea
                      value={policyForm}
                      onChange={e => setPolicyForm(e.target.value)}
                      rows={6}
                      className="cd-policy-textarea"
                      placeholder="e.g. Drivers must be aged 25–70, hold a full UK licence for at least 2 years, have no more than 6 penalty points…"
                    />
                    {policyError && <div className="cd-error">{policyError}</div>}
                    <div className="cd-btn-row">
                      <button className="btn btn-primary btn-sm" onClick={handleSavePolicy} disabled={policySaving}>
                        {policySaving ? 'Saving…' : 'Save Policy'}
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => { setEditingPolicy(false); setPolicyError(null); setPolicyForm(clientData.namedDriverPolicy || ''); }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Full driver policy table — shown only for Delifresh */}
                {clientData.companyName?.toLowerCase().includes('delifresh') && (
                  <div style={{ marginTop: 20 }}>
                    <NamedDriverPolicyBanner
                      additionalNotes={clientData.namedDriverPolicy}
                      isAdmin={true}
                    />
                  </div>
                )}
              </div>

              {/* Login Background Video */}
              <div className="cd-settings-block">
                <h3>🎬 Login Background Video</h3>
                <p className="cd-settings-desc">
                  Upload a video that plays as the background on the login splash screen for this client's users.
                  MP4, WebM or MOV · Max 100 MB.
                </p>
                {loginVideoFileName ? (
                  <div className="cd-video-section">
                    <div className="cd-video-filename">📹 {loginVideoFileName}</div>
                    {canEditOnboarding && (
                      <div className="cd-btn-row">
                        <button className="btn btn-secondary btn-sm" onClick={() => loginVideoInputRef.current?.click()} disabled={loginVideoUploading}>
                          {loginVideoUploading ? 'Uploading…' : 'Replace Video'}
                        </button>
                        <button className="cd-remove-btn" onClick={handleLoginVideoDelete}>Remove</button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="cd-btn-row">
                    <span className="cd-muted">No video uploaded.</span>
                    {canEditOnboarding && (
                      <button className="btn btn-primary btn-sm" onClick={() => loginVideoInputRef.current?.click()} disabled={loginVideoUploading}>
                        {loginVideoUploading ? 'Uploading…' : 'Upload Video'}
                      </button>
                    )}
                  </div>
                )}
                <input
                  ref={loginVideoInputRef}
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime"
                  style={{ display: 'none' }}
                  onChange={handleLoginVideoUpload}
                />
                {loginVideoError && <div className="cd-error">{loginVideoError}</div>}
                {loginVideoSuccess && <div className="cd-success">✓ Video uploaded successfully.</div>}
              </div>

            </div>
          )}

          {/* ── Notes ── */}
          {activeTab === 'notes' && (
            <div className="cd-notes">
              <h3>Client Notes</h3>
              <div className="cd-notes-display">
                {notes ? (
                  notes.split('\n').map((note, i) => (
                    <div key={i} className="cd-note-line">{note}</div>
                  ))
                ) : (
                  <p className="cd-muted" style={{ margin: 0 }}>No notes added yet.</p>
                )}
              </div>
              <div className="cd-notes-input">
                <textarea
                  placeholder="Add a new note…"
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  rows={3}
                />
                <button className="btn btn-primary" onClick={handleAddNote} disabled={!newNote.trim()}>
                  ➕ Add Note
                </button>
              </div>
            </div>
          )}

        </div>{/* end tab-content */}
      </div>{/* end tabs-container */}

    </div>
  );
};

export default ClientDetails;
